import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CameraIcon from "@mui/icons-material/CameraAlt";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

export default function CapturePhotoNonMobileDialog({
  open,
  setOpen,
  setAvatar,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [mediaStream, setMediaStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);

  useEffect(() => {
    if (open && !isIOS) {
      startWebcam();
    }
    return () => {
      stopWebcam();
    };
  }, [open]);

  const startWebcam = async () => {
    try {
      setCapturedImage(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setMediaStream(stream);
    } catch (error) {
      console.error("Error accessing webcam", error);
    }
  };

  const stopWebcam = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
    }
  };

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (video && canvas && context) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageDataUrl = canvas.toDataURL("image/jpeg");
      setCapturedImage(imageDataUrl);
      stopWebcam();
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result); // base64 image
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    if (!isIOS) {
      startWebcam();
    }
  };

  const handleSave = () => {
    console.log("Passing captured image to parent:", capturedImage);
    if (capturedImage) {
      setAvatar(capturedImage); // Save to parent
    }
    handleClose();
  };

  const handleClose = () => {
    stopWebcam();
    setOpen(false);
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="capture-photo-dialog"
      open={open}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="capture-photo-dialog">
        Capture Photo
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Box sx={{ textAlign: "center" }}>
          {capturedImage ? (
            <>
              <img
                src={capturedImage}
                alt="Captured"
                style={{ width: "100%", maxWidth: 300 }}
              />
              <Button variant="outlined" onClick={handleRetake} sx={{ mt: 2 }}>
                Retake Photo
              </Button>
              <Button
                onClick={handleSave}
                disabled={!capturedImage}
                variant="contained"
                sx={{ mt: 2, marginLeft: 2 }}
              >
                Keep
              </Button>
            </>
          ) : (
            <>
              {isIOS ? (
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileInput}
                />
              ) : (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    style={{ width: "100%", maxWidth: 300 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<CameraIcon />}
                    onClick={captureImage}
                    sx={{ mt: 2 }}
                  >
                    Capture
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!capturedImage}
                    variant="contained"
                    sx={{ mt: 2, marginLeft: 2 }}
                  >
                    Keep
                  </Button>
                </>
              )}
            </>
          )}
        </Box>

        <canvas ref={canvasRef} style={{ display: "none" }} />
      </DialogContent>

      <DialogActions>
        {/* <Button
          onClick={handleSave}
          disabled={!capturedImage}
          variant="contained"
        >
          Save
        </Button> */}
      </DialogActions>
    </BootstrapDialog>
  );
}
